import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import type { Notification } from '@traaker/shared';

interface NotificationsState {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  refresh: () => Promise<void>;
  markRead: (notificationId: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsState>({
  items: [],
  unreadCount: 0,
  loading: false,
  refresh: async () => {},
  markRead: async () => {},
  markAllRead: async () => {},
});

/** Poll every 60 seconds while the tab is visible — bell data is not time-critical. */
const POLL_INTERVAL_MS = 60_000;

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user, tenant } = useAuth();
  const [items, setItems] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!user || !tenant) return;
    setLoading(true);
    try {
      const data = await api.get<{ items: Notification[]; unreadCount: number }>('/notifications');
      setItems(data.items || []);
      setUnreadCount(data.unreadCount || 0);
    } catch {
      // Silent — bell is non-critical; leave previous state.
    } finally {
      setLoading(false);
    }
  }, [user, tenant]);

  const markRead = useCallback(
    async (notificationId: string) => {
      try {
        await api.post(`/notifications/${notificationId}/read`, {});
      } catch {
        return;
      }
      setItems((prev) =>
        prev.map((n) =>
          n.notificationId === notificationId && !n.readAt
            ? { ...n, readAt: new Date().toISOString() }
            : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    },
    [],
  );

  const markAllRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all', {});
    } catch {
      return;
    }
    const now = new Date().toISOString();
    setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(0);
  }, []);

  useEffect(() => {
    if (!user || !tenant) {
      setItems([]);
      setUnreadCount(0);
      return;
    }
    refresh();
    pollRef.current = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current !== null) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [user, tenant, refresh]);

  return (
    <NotificationsContext.Provider
      value={{ items, unreadCount, loading, refresh, markRead, markAllRead }}
    >
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
